import { useEffect, useState, useMemo } from "react";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import {
  Modal,
  Form,
  FormItem,
  useForm,
  Select,
  Message,
  Spin,
} from "@/frontend/components/common";
import {
  fixtureService,
  tournamentParticipantService,
  teamService,
  fixtureStatusService,
} from "@/frontend/services";
import type { TournamentParticipantResponseDTO, TeamResponseDTO, FixtureStatusResponseDTO } from "@/shared/dto";

interface FixtureCreateModalProps {
  open: boolean;
  seasonId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

interface ParticipantWithTeam {
  participantId: string;
  teamId: string;
  teamName: string;
}

export function FixtureCreateModal({
  open,
  seasonId,
  onCancel,
  onSuccess,
}: FixtureCreateModalProps) {
  const [form] = useForm();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [participants, setParticipants] = useState<ParticipantWithTeam[]>([]);
  const [statuses, setStatuses] = useState<FixtureStatusResponseDTO[]>([]);

  useEffect(() => {
    if (!open || !seasonId) return;

    setLoading(true);
    Promise.all([
      tournamentParticipantService.getAll({ seasonId }),
      teamService.getPaginate({ page: 1, pageSize: 500 }),
      fixtureStatusService.getPaginate({ page: 1, pageSize: 50 }),
    ])
      .then(([participantsRes, teamsRes, statusesRes]) => {
        const teams: TeamResponseDTO[] = teamsRes.success && teamsRes.data ? teamsRes.data.data : [];
        const teamMap = new Map(teams.map((t) => [t.id, t.name]));

        const mapped: ParticipantWithTeam[] = participantsRes
          .filter((p) => p.teamId)
          .map((p) => ({
            participantId: p.id,
            teamId: p.teamId!,
            teamName: teamMap.get(p.teamId!) || p.teamId!,
          }));

        setParticipants(mapped);

        if (statusesRes.success && statusesRes.data) {
          setStatuses(statusesRes.data.data || []);
        }
      })
      .catch(() => {
        Message.error("Error al cargar datos");
      })
      .finally(() => setLoading(false));
  }, [open, seasonId]);

  const teamOptions = useMemo(
    () =>
      participants.map((p) => ({
        value: p.participantId,
        label: p.teamName,
      })),
    [participants],
  );

  const scheduledStatus = useMemo(
    () => statuses.find((s) => s.id === "scheduled" || s.name?.toLowerCase() === "programado"),
    [statuses],
  );

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const values = await form.validateFields();

      const matchDate = values.matchDate?.toDate?.() || new Date(values.matchDate);

      const response = await fixtureService.create({
        seasonId,
        homeParticipantId: values.homeParticipantId,
        awayParticipantId: values.awayParticipantId,
        matchDate: matchDate.toISOString(),
        statusId: scheduledStatus?.id ?? null,
      });

      if (response.success) {
        Message.success("Partido creado correctamente");
        form.resetFields();
        onSuccess();
      } else {
        Message.error("Error al crear el partido: " + (response as any).error);
      }
    } catch (error) {
      Message.error("Error al crear el partido");
      console.error("Error al crear el partido:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Agregar Partido"
      open={open}
      onCancel={handleCancel}
      onOk={handleSave}
      confirmLoading={saving}
      okText="Crear"
      cancelText="Cancelar"
      width={500}
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spin tip="Cargando equipos..." />
        </div>
      ) : (
        <Form form={form} layout="vertical" className="mt-4">
          <FormItem
            name="homeParticipantId"
            label="Equipo Local"
            rules={[{ required: true, message: "Selecciona el equipo local" }]}
          >
            <Select
              placeholder="Seleccionar equipo local"
              options={teamOptions}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </FormItem>

          <FormItem
            name="awayParticipantId"
            label="Equipo Visitante"
            rules={[{ required: true, message: "Selecciona el equipo visitante" }]}
          >
            <Select
              placeholder="Seleccionar equipo visitante"
              options={teamOptions}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </FormItem>

          <FormItem
            name="matchDate"
            label="Fecha y Hora"
            rules={[{ required: true, message: "Selecciona la fecha y hora" }]}
          >
            <DatePicker
              showTime
              format="DD/MM/YYYY HH:mm"
              className="w-full"
              placeholder="Seleccionar fecha y hora"
              disabledDate={(current) => current && current < dayjs().startOf("day")}
            />
          </FormItem>
        </Form>
      )}
    </Modal>
  );
}
